/**
 * useCabinAcademyStats — client-side aggregate hook
 *
 * Fires 5 parallel useQuery calls (page=1, limit=1 for totals + page=1, limit=200 for breakdowns).
 * Aggregates counts and category/level distributions client-side.
 * Falls back gracefully when individual endpoints are not yet live.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';

// ─── tiny helpers ─────────────────────────────────────────────────────────────

function buildCountParams(filters = {}) {
  const p = new URLSearchParams();
  p.set('page', '1');
  p.set('limit', '200'); // large enough to count everything
  Object.entries(filters).forEach(([k, v]) => {
    if (v != null && v !== '') p.set(k, String(v));
  });
  return p.toString();
}

function countByField(items, field) {
  const map = {};
  (items || []).forEach((item) => {
    const val = item[field] || 'unknown';
    map[val] = (map[val] || 0) + 1;
  });
  return map;
}

function latestDate(items, dateField = 'updated_at') {
  let latest = null;
  (items || []).forEach((item) => {
    const d = item[dateField] || item['created_at'];
    if (!d) return;
    if (!latest || d > latest) latest = d;
  });
  return latest;
}

// ─── individual fetchers ──────────────────────────────────────────────────────

async function fetchVoiceScenarios() {
  const res = await apiClient.get(`/admin/voice-practice/scenarios?${buildCountParams()}`);
  return res.data;
}

async function fetchVoiceInterviews() {
  const res = await apiClient.get(`/admin/voice-interview/questions?${buildCountParams()}`);
  return res.data;
}

async function fetchAviationScenarios() {
  const res = await apiClient.get(`/admin/aviation-english/scenarios?${buildCountParams()}`);
  return res.data;
}

async function fetchAnnounceTemplates() {
  const res = await apiClient.get(`/admin/announce-templates?${buildCountParams()}`);
  return res.data;
}

async function fetchPaxComm() {
  const res = await apiClient.get(`/admin/pax-comm/situations?${buildCountParams()}`);
  return res.data;
}

// ─── hook ─────────────────────────────────────────────────────────────────────

export function useCabinAcademyStats() {
  const voiceQ = useQuery({
    queryKey: ['cabinHub', 'voiceScenarios'],
    queryFn: fetchVoiceScenarios,
    staleTime: 60_000,
    retry: 1,
  });

  const interviewQ = useQuery({
    queryKey: ['cabinHub', 'voiceInterviews'],
    queryFn: fetchVoiceInterviews,
    staleTime: 60_000,
    retry: 1,
  });

  const aviationQ = useQuery({
    queryKey: ['cabinHub', 'aviationScenarios'],
    queryFn: fetchAviationScenarios,
    staleTime: 60_000,
    retry: 1,
  });

  const announceQ = useQuery({
    queryKey: ['cabinHub', 'announceTemplates'],
    queryFn: fetchAnnounceTemplates,
    staleTime: 60_000,
    retry: 1,
  });

  const paxQ = useQuery({
    queryKey: ['cabinHub', 'paxComm'],
    queryFn: fetchPaxComm,
    staleTime: 60_000,
    retry: 1,
  });

  // ── derive stats per module ────────────────────────────────────────────────

  const voiceItems = voiceQ.data?.items || voiceQ.data?.scenarios || voiceQ.data?.data || [];
  const interviewItems = interviewQ.data?.items || interviewQ.data?.questions || interviewQ.data?.data || [];
  const aviationItems = aviationQ.data?.items || aviationQ.data?.scenarios || aviationQ.data?.data || [];
  const announceItems = announceQ.data?.items || announceQ.data?.templates || announceQ.data?.data || [];
  const paxItems = paxQ.data?.items || paxQ.data?.situations || paxQ.data?.data || [];

  // Voice Practice — breakdown by level
  const voiceStats = {
    total: voiceQ.data?.total ?? voiceItems.length,
    byLevel: countByField(voiceItems, 'level'),
    byCategory: countByField(voiceItems, 'category'),
    lastUpdated: latestDate(voiceItems),
    isLoading: voiceQ.isLoading,
    isError: voiceQ.isError,
  };

  // Voice Interview — breakdown by difficulty
  const interviewStats = {
    total: interviewQ.data?.total ?? interviewItems.length,
    byDifficulty: countByField(interviewItems, 'difficulty'),
    byType: countByField(interviewItems, 'question_type'),
    lastUpdated: latestDate(interviewItems),
    isLoading: interviewQ.isLoading,
    isError: interviewQ.isError,
  };

  // Aviation English — scenarios + question count embedded
  const aviationStats = {
    total: aviationQ.data?.total ?? aviationItems.length,
    byLevel: countByField(aviationItems, 'level'),
    totalQuestions: aviationItems.reduce((acc, s) => acc + (s.question_count || 0), 0),
    lastUpdated: latestDate(aviationItems),
    isLoading: aviationQ.isLoading,
    isError: aviationQ.isError,
  };

  // Announce Templates — breakdown by category
  const announceStats = {
    total: announceQ.data?.total ?? announceItems.length,
    byCategory: countByField(announceItems, 'category'),
    lastUpdated: latestDate(announceItems),
    isLoading: announceQ.isLoading,
    isError: announceQ.isError,
  };

  // Pax Comm — breakdown by category
  const paxStats = {
    total: paxQ.data?.total ?? paxItems.length,
    byCategory: countByField(paxItems, 'category'),
    lastUpdated: latestDate(paxItems),
    isLoading: paxQ.isLoading,
    isError: paxQ.isError,
  };

  // ── aggregate metrics ──────────────────────────────────────────────────────

  const totalAcrossModules =
    voiceStats.total +
    interviewStats.total +
    aviationStats.total +
    announceStats.total +
    paxStats.total;

  const moduleTotals = [
    { id: 'voicePractice', total: voiceStats.total },
    { id: 'voiceInterview', total: interviewStats.total },
    { id: 'aviationEnglish', total: aviationStats.total },
    { id: 'announceTemplates', total: announceStats.total },
    { id: 'paxComm', total: paxStats.total },
  ];

  const mostActive = moduleTotals.reduce((a, b) => (b.total > a.total ? b : a), moduleTotals[0]);

  const allDates = [
    voiceStats.lastUpdated,
    interviewStats.lastUpdated,
    aviationStats.lastUpdated,
    announceStats.lastUpdated,
    paxStats.lastUpdated,
  ].filter(Boolean);

  const lastUpdatedGlobal = allDates.length
    ? allDates.reduce((a, b) => (b > a ? b : a))
    : null;

  const isAnyLoading =
    voiceQ.isLoading ||
    interviewQ.isLoading ||
    aviationQ.isLoading ||
    announceQ.isLoading ||
    paxQ.isLoading;

  return {
    isLoading: isAnyLoading,
    voiceStats,
    interviewStats,
    aviationStats,
    announceStats,
    paxStats,
    aggregate: {
      totalAcrossModules,
      mostActive,
      lastUpdatedGlobal,
      // Hata vermeyen (total > 0 VEYA yuklenmediyse sayma) modul sayisi.
      // CabinAiCoach placeholder'i sayilmaz — gercek icerik modullerini say.
      activeModules: moduleTotals.filter((m) => !m.isError).length,
    },
  };
}
